'use client';

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

export type ActivityType =
  | 'document_created'
  | 'document_edited' |'document_deleted' |'document_status_changed' |'email_sent' |'email_delivered' |'email_failed' |'pdf_downloaded' |'user_login' |'user_logout' |'settings_updated' |'bulk_action';

export type ActivitySeverity = 'info' | 'success' | 'warning' | 'error';

export interface ActivityLogEntry {
  id: string;
  type: ActivityType;
  severity: ActivitySeverity;
  message: string;
  detail?: string;
  user: string;
  userRole?: string;
  docNumber?: string;
  docType?: string;
  client?: string;
  recipientEmail?: string;
  senderEmail?: string;
  timestamp: string;
  metadata?: Record<string, string | number | boolean>;
}

interface ActivityLogContextValue {
  logs: ActivityLogEntry[];
  addLog: (entry: Omit<ActivityLogEntry, 'id' | 'timestamp'>) => void;
  clearLogs: () => void;
}

const ActivityLogContext = createContext<ActivityLogContextValue>({
  logs: [],
  addLog: () => {},
  clearLogs: () => {},
});

function rowToLog(row: any): ActivityLogEntry {
  return {
    id: row.id,
    type: row.activity_type as ActivityType,
    severity: row.severity as ActivitySeverity,
    message: row.message,
    detail: row.detail || undefined,
    user: row.user_name,
    userRole: row.user_role || undefined,
    docNumber: row.doc_number || undefined,
    docType: row.doc_type || undefined,
    client: row.client || undefined,
    recipientEmail: row.recipient_email || undefined,
    senderEmail: row.sender_email || undefined,
    timestamp: row.created_at,
    metadata: row.metadata || undefined,
  };
}

export function ActivityLogProvider({ children }: { children: React.ReactNode }) {
  const [logs, setLogs] = useState<ActivityLogEntry[]>([]);
  const supabase = createClient();

  useEffect(() => {
    supabase
      .from('activity_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200)
      .then(({ data }) => {
        if (data) setLogs(data.map(rowToLog));
      })
      .catch(() => {});
  }, []);

  const addLog = useCallback(async (entry: Omit<ActivityLogEntry, 'id' | 'timestamp'>) => {
    const optimisticEntry: ActivityLogEntry = {
      ...entry,
      id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
    };
    setLogs(prev => [optimisticEntry, ...prev]);

    try {
      const { data } = await supabase
        .from('activity_logs')
        .insert({
          activity_type: entry.type,
          severity: entry.severity,
          message: entry.message,
          detail: entry.detail || null,
          user_name: entry.user,
          user_role: entry.userRole || null,
          doc_number: entry.docNumber || null,
          doc_type: entry.docType || null,
          client: entry.client || null,
          recipient_email: entry.recipientEmail || null,
          sender_email: entry.senderEmail || null,
          metadata: entry.metadata || null,
        })
        .select()
        .single();

      if (data) {
        setLogs(prev => prev.map(l => l.id === optimisticEntry.id ? rowToLog(data) : l));
      }
    } catch {
      // Keep optimistic entry on error
    }
  }, [supabase]);

  const clearLogs = useCallback(async () => {
    setLogs([]);
    try {
      await supabase.from('activity_logs').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    } catch {
      // Silently fail
    }
  }, [supabase]);

  const value = useMemo(() => ({ logs, addLog, clearLogs }), [logs, addLog, clearLogs]);

  return (
    <ActivityLogContext.Provider value={value}>
      {children}
    </ActivityLogContext.Provider>
  );
}

export function useActivityLog() {
  return useContext(ActivityLogContext);
}
